import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ShieldCheck, CheckCircle, XCircle, Clock, User, EnvelopeSimple, Phone, Buildings, MapPin } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  pending: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.25)', color: '#fbbf24', label: 'Pending', icon: Clock },
  approved: { bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.25)', color: '#4ade80', label: 'Approved', icon: CheckCircle },
  rejected: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.25)', color: '#f87171', label: 'Rejected', icon: XCircle },
};

const AdminApproval = () => {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [processing, setProcessing] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchUsers(); }, [open]);

  const handleApprove = async (userId, name) => {
    setProcessing(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`);
      toast.success(`${name} approved`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const handleReject = async (userId, name) => {
    if (!window.confirm(`Reject ${name}'s profile?`)) return;
    setProcessing(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`);
      toast.success(`${name} rejected`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const nonAdminUsers = users.filter(u => u.role !== 'admin');
  const pendingCount = nonAdminUsers.filter(u => u.verification_status === 'pending' || (!u.verified && !u.verification_status)).length;
  const filtered = filter === 'all' ? nonAdminUsers : nonAdminUsers.filter(u => {
    if (filter === 'pending') return u.verification_status === 'pending' || (!u.verified && !u.verification_status);
    return u.verification_status === filter;
  });

  return (
    <>
      <button
        data-testid="admin-approval-button"
        onClick={() => setOpen(true)}
        className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
        style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.15)', color: 'var(--gold)' }}
      >
        <ShieldCheck size={16} weight="bold" />
        Profiles
        {pendingCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center text-[10px] font-bold rounded-full" style={{ background: '#ef4444', color: '#fff' }}>{pendingCount}</span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-heading flex items-center gap-2" style={{ color: 'var(--gold-light)' }}>
              <ShieldCheck size={22} weight="duotone" style={{ color: 'var(--gold)' }} /> Profile Approvals
            </DialogTitle>
          </DialogHeader>

          {/* Filter tabs */}
          <div className="flex rounded-lg overflow-hidden my-3" style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)' }}>
            {[{ id: 'all', label: 'All' }, { id: 'pending', label: `Pending (${pendingCount})` }, { id: 'approved', label: 'Approved' }, { id: 'rejected', label: 'Rejected' }].map(tab => (
              <button key={tab.id} data-testid={`filter-${tab.id}`} onClick={() => setFilter(tab.id)}
                className="flex-1 py-2 text-xs font-semibold transition-all"
                style={{ background: filter === tab.id ? 'rgba(201,206,211,0.12)' : 'transparent', color: filter === tab.id ? 'var(--gold-light)' : 'var(--text-muted)' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* User list */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {loading ? (
              <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>No {filter === 'all' ? '' : filter} profiles</div>
            ) : (
              filtered.map(u => {
                const vs = u.verification_status || (u.verified ? 'approved' : 'pending');
                const st = STATUS_STYLE[vs] || STATUS_STYLE.pending;
                const Icon = st.icon;
                return (
                  <div key={u.id} data-testid={`user-card-${u.id}`} className="rounded-lg p-4" style={{ background: 'var(--bg-elevated)', border: `1px solid ${st.border}` }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{u.name}</span>
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                            {u.role}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }}>
                            <Icon size={10} weight="bold" /> {st.label}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                          {u.company && <span className="flex items-center gap-1"><Buildings size={11} /> {u.company}</span>}
                          <span className="flex items-center gap-1"><EnvelopeSimple size={11} /> {u.email}</span>
                          {u.phone && <span className="flex items-center gap-1"><Phone size={11} /> {u.phone}</span>}
                          {u.location && <span className="flex items-center gap-1"><MapPin size={11} /> {u.location}</span>}
                          {u.state_license_number && <span className="flex items-center gap-1">License: {u.state_license_number}</span>}
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {vs !== 'approved' && (
                          <Button data-testid={`approve-${u.id}`} onClick={() => handleApprove(u.id, u.name)} disabled={processing === u.id} size="sm" className="text-xs gap-1"
                            style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}>
                            <CheckCircle size={13} weight="bold" /> Approve
                          </Button>
                        )}
                        {vs !== 'rejected' && vs !== 'approved' && (
                          <Button data-testid={`reject-${u.id}`} onClick={() => handleReject(u.id, u.name)} disabled={processing === u.id} size="sm" className="text-xs gap-1"
                            style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}>
                            <XCircle size={13} weight="bold" /> Reject
                          </Button>
                        )}
                        {vs === 'approved' && (
                          <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#4ade80' }}><CheckCircle size={14} weight="fill" /> Approved</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminApproval;
